import React from "react";
import { ArrowRight, ShieldCheck, Layers, WifiOff } from "lucide-react";
import { InnovriddhiMark } from "./InnovriddhiMark";

export const Hero: React.FC = () => {
  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden bg-canvas pt-12 pb-20 lg:pt-20 lg:pb-28 border-b border-charcoal/5">
      {/* Background Lattice Watermark */}
      <div
        className="absolute top-1/2 -right-24 -translate-y-1/2 pointer-events-none opacity-[0.035] text-plum select-none"
        aria-hidden="true"
      >
        <InnovriddhiMark size={640} form="interlaced" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          {/* Eyebrow Pill */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cream-300/60 border border-charcoal/10 text-xs font-mono font-medium text-plum tracking-wide uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-plum animate-pulse" />
            Corporate Entity · Software for Field Operations
          </div>

          {/* Central Brand Accent */}
          <div className="flex justify-center pt-2">
            <div className="relative p-3 rounded-2xl bg-white/70 shadow-sm border border-charcoal/5">
              <div className="text-plum glow-plum rounded-xl transition-transform duration-300 hover:scale-105">
                <InnovriddhiMark size={64} form="interlaced" />
              </div>
            </div>
          </div>

          {/* Main Headline */}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ink tracking-tight leading-[1.15]">
            Operational clarity for businesses that{" "}
            <span className="text-plum underline decoration-plum/20 underline-offset-8">
              run in the field.
            </span>
          </h1>

          {/* Mission Subtitle */}
          <p className="text-lg sm:text-xl text-ink-secondary leading-relaxed max-w-2xl mx-auto font-normal">
            Innovriddhi builds high-reliability, audit-grade software platforms
            engineered for the complex realities of Indian supply chain
            distribution and distributed field workforces.
          </p>

          {/* Action CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <button
              onClick={() => scrollTo("#products")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-base font-semibold text-white bg-plum hover:bg-plum-hover shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-plum"
            >
              Explore Products
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => scrollTo("#contact")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-base font-semibold text-ink bg-white/80 hover:bg-white border border-charcoal/15 hover:border-charcoal/30 shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-charcoal"
            >
              Contact Enterprise Team
            </button>
          </div>

          {/* Proof Pillars / Key Markers */}
          <div className="pt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-charcoal/10 text-left">
            <div className="p-4 rounded-xl bg-white/50 border border-charcoal/5 flex items-start gap-3.5">
              <div className="p-2 rounded-lg bg-plum/10 text-plum shrink-0">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display text-sm font-semibold text-ink">
                  4-Tier Supply Chain
                </h3>
                <p className="text-xs text-ink-secondary mt-0.5">
                  Complete visibility from Warehouse to Stockist, Salesperson,
                  and Retailer.
                </p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-white/50 border border-charcoal/5 flex items-start gap-3.5">
              <div className="p-2 rounded-lg bg-pine/10 text-pine shrink-0">
                <WifiOff className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display text-sm font-semibold text-ink">
                  Offline-First Engine
                </h3>
                <p className="text-xs text-ink-secondary mt-0.5">
                  Built to capture visits, orders, and telemetry without
                  dropping rural pings.
                </p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-white/50 border border-charcoal/5 flex items-start gap-3.5">
              <div className="p-2 rounded-lg bg-plum/10 text-plum shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display text-sm font-semibold text-ink">
                  Audit-Grade Traceability
                </h3>
                <p className="text-xs text-ink-secondary mt-0.5">
                  Per-tenant database isolation, immutable action logs, and
                  tamper-resistant audit trails.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
